import {
  ApplyingOptionMap,
  DifficultCohabitingFamily,
  DifficultExcretionAssistances,
  DifficultGrades,
  DifficultHouseworkAssistances,
  DifficultMealAssistances,
  DifficultMovementAssistances,
  GradeMap,
  HighWageMap,
  WelfareTypeMap,
} from './constants'
import {
  BbasJobPostingForBusiness,
  DifficultyStatus,
  JobPosting,
  JobPostingFormData,
  JobPostingMatchingRateProperty,
} from './interfaces'
import {
  JobSearchTime,
  JobSearchTimeHourMap,
  JobSearchTimeMap,
  PreferDistance,
  PreferDistanceMap,
  PreferWorkType,
  User,
} from '../user'
import { Gender, Grade, PayType, WorkType } from './types'
import { WeekDay, isNil } from '../shared'

export const getHighWageInfo = ({
  workType,
  payType,
  wage,
}: {
  workType: WorkType
  payType: PayType
  wage: number
}) => {
  const info = HighWageMap[workType]

  if (!info) {
    return {
      satisfied: false,
    }
  }

  let satisfied = false

  // 방문목욕 다인 케이스의 경우 월급으로 계산하기에 예외처리
  if (workType === 'bath_help' && payType === 'monthly' && wage >= 2_400_000) {
    satisfied = true
  }

  return {
    satisfied: satisfied || (wage >= info.wage && info.payType === payType),
    payType: info.payType,
    workType: workType,
    wage: info.wage,
  }
}

export const getBadgeInfo = ({
  welfareTypes,
  applyingOptions,
  canNegotiateWorkTime,
  workType,
  payType,
  wage,
}: {
  welfareTypes?: string[]
  applyingOptions?: string[]
  canNegotiateWorkTime?: boolean
  workType: WorkType
  payType: PayType
  wage: number
}) => ({
  highWage: getHighWageInfo({ workType, payType, wage }).satisfied,
  canNegotiateWorkTime,
  transportationExpenses: welfareTypes?.includes(WelfareTypeMap.TransportationExpenses) ?? false,
  canApplyNewbie: applyingOptions?.includes(ApplyingOptionMap.Newbie) ?? false,
})

export const getMatchingProperties = ({
  user,
  jobPosting,
}: {
  user?: User
  jobPosting: JobPosting
}) => ({
  title: jobPosting.title,
  centerName: jobPosting.center_name,
  jobPostingId: jobPosting.public_id,
  ...getBadgeInfo({
    welfareTypes: jobPosting.welfare_types ?? [],
    applyingOptions: jobPosting.applying_options ?? [],
    canNegotiateWorkTime: jobPosting.can_negotiate_work_time,
    workType: jobPosting.work_type,
    payType: jobPosting.pay_type,
    wage: jobPosting.max_wage,
  }),
  ...(isNil(user) ? {} : getMatchInfo(user, jobPosting)),
})

export const getMatchingPropertiesV2 = ({
  user,
  jobPosting,
}: {
  user?: User
  jobPosting: JobPostingMatchingRateProperty
}) => ({
  title: jobPosting.title,
  centerName: jobPosting.centerName,
  jobPostingId: jobPosting.publicId,
  ...getBadgeInfo({
    welfareTypes: jobPosting.welfareTypes ?? [],
    applyingOptions: jobPosting.applyingOptions ?? [],
    canNegotiateWorkTime: jobPosting.canNegotiateWorkTime,
    workType: jobPosting.workType,
    payType: jobPosting.payType,
    wage: jobPosting.maxWage,
  }),
  ...getDifficultyStatusV2(jobPosting),
  ...(isNil(user) ? {} : getMatchInfoV2(user, jobPosting)),
})

export function isTimeMatch({
  workStartTime,
  workEndTime,
  jobSearchTimes,
}: {
  workStartTime?: string
  workEndTime?: string
  jobSearchTimes?: JobSearchTime[]
}) {
  if ([workStartTime, workEndTime, jobSearchTimes].some(isNil)) {
    return null
  }

  const startTime = parseInt(workStartTime?.split(':')[0] ?? '0')
  const endTime = parseInt(workEndTime?.split(':')[0] ?? '0')
  return [
    JobSearchTimeMap.Morning,
    JobSearchTimeMap.EarlyAfternoon,
    JobSearchTimeMap.LateAfternoon,
    JobSearchTimeMap.Evening,
  ]
    .filter((time) => jobSearchTimes?.includes(time))
    .reduce<Array<[number, number]>>((acc, cur) => {
      const [startHour, endHour] = JobSearchTimeHourMap[cur]

      if (acc?.length < 1) {
        return [[startHour, endHour]]
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const [lastStartHour, lastEndHour] = acc.pop()!

      if (lastEndHour === startHour) {
        acc.push([lastStartHour, endHour])
      } else {
        acc.push([lastStartHour, lastEndHour], [startHour, endHour])
      }
      return acc
    }, [])
    .some(([startHour, endHour]) => {
      return startTime >= startHour && endTime <= endHour
    })
}

export const isDayMatch = (jobSearchDays?: WeekDay[], workingDays?: WeekDay[]) => {
  if (!jobSearchDays || !workingDays) {
    return null
  }

  return workingDays.every((day) => {
    return jobSearchDays.includes(day)
  })
}

export const isGenderMatch = (preferredGender?: Gender | 'all', customerGender?: string) => {
  if (isNil(preferredGender) || isNil(customerGender)) {
    return null
  }

  if (preferredGender === 'all') {
    return true
  }

  return preferredGender === customerGender
}

export const isTypeMatch = (preferredWorkTypes?: PreferWorkType[], jobWorkType?: WorkType) => {
  if (isNil(preferredWorkTypes) || isNil(jobWorkType)) {
    return null
  }

  return preferredWorkTypes?.includes(jobWorkType as any)
}

export const isGradeMatch = (
  preferredGrades?: Omit<Grade, (typeof GradeMap)['None']>[],
  customerGrade?: string
) => {
  if (isNil(preferredGrades) || isNil(customerGrade)) {
    return null
  }

  return preferredGrades?.includes?.(customerGrade as any)
}

export const isDistanceMatch = (preferredDistance?: PreferDistance, distance?: string) => {
  const distanceRegexp = /도보 (?<minute>[\w]{1,2})분 (이내|이상)/gi

  if (isNil(preferredDistance) || isNil(distance)) {
    return null
  }

  const result = distanceRegexp.exec(distance as string)

  if (isNil(result?.groups?.minute) || typeof result?.groups?.minute !== 'string') {
    return null
  }

  const minute = parseInt(result.groups?.minute)

  if (preferredDistance === PreferDistanceMap.ByWalk15) return minute < 15
  if (preferredDistance === PreferDistanceMap.ByWalk30) return minute < 30

  return null
}

export interface MatchInfo {
  time_match?: boolean | null
  day_match?: boolean | null
  type_match?: boolean | null
  gender_match?: boolean | null
  grade_match?: boolean | null
  distance_match?: boolean | null
}

export const getMatchInfo = (user: User, job?: JobPosting): MatchInfo => {
  const result: MatchInfo = {
    time_match: null,
    day_match: null,
    type_match: null,
    gender_match: null,
    grade_match: null,
  }

  try {
    result.time_match = isTimeMatch({
      workStartTime: job?.work_start_time,
      workEndTime: job?.work_end_time,
      jobSearchTimes: user.job_search_times,
    })
    result.type_match = isTypeMatch(user.preferred_work_types, job?.work_type)
    result.day_match = isDayMatch(user.job_search_days, job?.working_days as WeekDay[])
    result.gender_match = isGenderMatch(user.preferred_gender, job?.job_posting_customer?.gender)
    result.grade_match = isGradeMatch(user.preferred_grades, job?.job_posting_customer?.grade)

    return result
  } catch (e) {
    return result
  }
}

export const getMatchInfoV2 = (user: any, job: any): MatchInfo => {
  const result: MatchInfo = {
    time_match: null,
    day_match: null,
    type_match: null,
    gender_match: null,
    grade_match: null,
  }

  try {
    result.time_match = isTimeMatch({
      workStartTime: job?.workStartTime,
      workEndTime: job?.workEndTime,
      jobSearchTimes: user.jobSearchTimes,
    })
    result.type_match = isTypeMatch(user.preferred_work_types, job?.workType)
    result.day_match = isDayMatch(user.job_search_days, job?.workingDays as WeekDay[])
    result.gender_match = isGenderMatch(user.preferred_gender, job?.gender)
    result.grade_match = isGradeMatch(user.preferred_grades, job?.grade)

    return result
  } catch (e) {
    return result
  }
}

export const getMatchInfoV2onBbas = (user: any, job: any): MatchInfo => {
  const result: MatchInfo = {
    time_match: null,
    day_match: null,
    type_match: null,
    gender_match: null,
    grade_match: null,
  }

  try {
    result.time_match = isTimeMatch({
      workStartTime: job?.workStartTime,
      workEndTime: job?.workEndTime,
      jobSearchTimes: user.jobSearchTimes,
    })
    result.type_match = isTypeMatch(user.preferredWorkTypes, job?.workType)
    result.day_match = isDayMatch(user.jobSearchDays, job?.workingDays as WeekDay[])
    result.gender_match = isGenderMatch(user.preferredGender, job?.gender)
    result.grade_match = isGradeMatch(user.preferredGrades, job?.grade)

    return result
  } catch (e) {
    return result
  }
}

export const getDifficultyStatus = (job: JobPostingFormData): DifficultyStatus => {
  const { jobPostingCustomer } = job
  const {
    grade,
    mealAssistances,
    movementAssistances,
    excretionAssistances,
    houseworkAssistances,
    cohabitingFamily,
  } = jobPostingCustomer ?? {}

  return {
    isDifficultGrade: DifficultGrades.includes(grade),
    isDifficultMealAssistance:
      mealAssistances?.some?.((i) => DifficultMealAssistances.includes(i)) ?? false,
    isDifficultExcretionAssistance:
      excretionAssistances?.some?.((i) => DifficultExcretionAssistances.includes(i)) ?? false,
    isDifficultMovementAssistance:
      movementAssistances?.some?.((i) => DifficultMovementAssistances.includes(i)) ?? false,
    isDifficultHouseWork:
      houseworkAssistances?.some?.((i) => DifficultHouseworkAssistances.includes(i)) ?? false,
    isDifficultCohabitingFamily: DifficultCohabitingFamily.includes(cohabitingFamily) ?? false,
  }
}

export const getDifficultyStatusV2 = (job: JobPostingMatchingRateProperty) => {
  const { jobPostingCustomer } = job ?? {}
  const {
    grade,
    mealAssistances,
    movementAssistances,
    excretionAssistances,
    houseworkAssistances,
    cohabitingFamily,
  } = jobPostingCustomer ?? {}

  return {
    isDifficultGrade: DifficultGrades.includes(grade),
    isDifficultMealAssistance:
      mealAssistances?.some?.((i) => DifficultMealAssistances.includes(i)) ?? false,
    isDifficultExcretionAssistance:
      excretionAssistances?.some?.((i) => DifficultExcretionAssistances.includes(i)) ?? false,
    isDifficultMovementAssistance:
      movementAssistances?.some?.((i) => DifficultMovementAssistances.includes(i)) ?? false,
    isDifficultHouseWork:
      houseworkAssistances?.some?.((i) => DifficultHouseworkAssistances.includes(i)) ?? false,
    isDifficultCohabitingFamily: DifficultCohabitingFamily.includes(cohabitingFamily) ?? false,
  }
}

export const isLightningJobPosting = (jobPosting: BbasJobPostingForBusiness): boolean => {
  if (!jobPosting?.paidFeatures) {
    return false
  }

  if (!jobPosting?.paidFeatures.length) {
    return false
  }

  return jobPosting.paidFeatures.some(
    (feature) => feature.feature === 'init' || feature.feature === 'area'
  )
}

export const isPaidFeatureOn = (
  jobPosting: Pick<BbasJobPostingForBusiness, 'paidFeatures'>,
  searchFeature: string
) => {
  if (!jobPosting?.paidFeatures) {
    return false
  }

  return jobPosting.paidFeatures.some((feature) => searchFeature === feature.feature)
}

export const isPaidFeatureVoucherAppliedJobPosting = (
  jobPosting: BbasJobPostingForBusiness,
  searchFeature: string
) => {
  if (!jobPosting?.paidFeatures) return false

  const feature = jobPosting.paidFeatures.find((f) => f.feature === searchFeature)

  if (!feature) return false

  return !feature.paidStatus
}

export const paidFeaturePaymentStatus = (
  jobPosting: BbasJobPostingForBusiness,
  searchFeature: string
) => {
  if (!jobPosting?.paidFeatures) {
    return undefined
  }

  const matchedFeature = jobPosting.paidFeatures.find(
    (feature) => searchFeature === feature.feature
  )
  return matchedFeature?.paidStatus
}

export const isPaidFeatureWaitingForDeposit = (
  jobPosting: BbasJobPostingForBusiness,
  searchFeature: string
) => {
  return paidFeaturePaymentStatus(jobPosting, searchFeature) === 'WAITING_FOR_DEPOSIT'
}
